/**
 * Premium "Cinematic Reveal" intro.
 *
 * Visual design:
 *  - Two horizontal letterbox bars retract (reveal animation), exposing a
 *    centered title with film-grain backdrop.
 *  - Light leak sweeps from left to right, then dims into a soft glow.
 *  - Date/Production-style timecode chip top-right.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const cinematicReveal: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver Presents";
  const sub = inputs.subtext ?? "";
  const tag = String(inputs.extras?.tag ?? "REEL · 01");
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;
  const tagSize = typeSize("sm", ctx.format) * 0.6;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const topBarId = `${clipPrefix}-bar-top`;
  const botBarId = `${clipPrefix}-bar-bot`;
  const leakId = `${clipPrefix}-leak`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;
  const tagId = `${clipPrefix}-tag`;
  const tcId = `${clipPrefix}-tc`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div id="${topBarId}" style="position:absolute;left:0;right:0;top:0;height:50%;background:${themeColors.bg};z-index:5;border-bottom:1px solid ${withAlpha(accent, 0.45)};box-shadow:0 8px 24px rgba(0,0,0,0.6);"></div>
  <div id="${botBarId}" style="position:absolute;left:0;right:0;bottom:0;height:50%;background:${themeColors.bg};z-index:5;border-top:1px solid ${withAlpha(accent, 0.45)};box-shadow:0 -8px 24px rgba(0,0,0,0.6);"></div>

  <div id="${leakId}" style="position:absolute;inset:0;background:linear-gradient(105deg, transparent 30%, ${withAlpha(themeColors.fg, 0.85)} 47%, ${withAlpha(accent, 0.95)} 50%, ${withAlpha(themeColors.fg, 0.85)} 53%, transparent 70%);transform:translateX(-120%);mix-blend-mode:screen;filter:blur(8px);pointer-events:none;z-index:6;"></div>

  <div id="${tcId}" style="position:absolute;top:48px;right:64px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${tagSize}px;letter-spacing:3px;color:${withAlpha(themeColors.fg, 0.7)};opacity:0;z-index:7;">
    <span style="display:inline-block;width:10px;height:10px;background:${accent};border-radius:9999px;margin-right:10px;box-shadow:0 0 12px ${accent};"></span>REC · ${escapeHtml(tag)}
  </div>

  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;z-index:4;">
    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— Cinematic Reveal —</div>
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.5px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;">${escapeHtml(sub)}</div>` : ""}
    <div id="${tagId}" style="display:flex;align-items:center;gap:14px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${tagSize}px;letter-spacing:6px;text-transform:uppercase;color:${withAlpha(themeColors.fg, 0.55)};opacity:0;">
      <span style="width:18px;height:1px;background:${withAlpha(themeColors.fg, 0.55)};"></span>
      <span>An Allyver Production</span>
      <span style="width:18px;height:1px;background:${withAlpha(themeColors.fg, 0.55)};"></span>
    </div>
  </div>
</div>`;

  const init = [
    `gsap.set("#${headId}", { opacity: 0, scale: 1.08 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 14 });` : "",
    `gsap.set("#${eyeId}", { opacity: 0, y: -10 });`,
    `gsap.set("#${tagId}", { opacity: 0, y: 14 });`,
    `gsap.set("#${tcId}", { opacity: 0 });`,
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    // Bars retract.
    t.to(`#${topBarId}`, { y: "-100%", duration: 0.85, ease: "raw:\"power3.inOut\"" }, 0.1),
    t.to(`#${botBarId}`, { y: "100%", duration: 0.85, ease: "raw:\"power3.inOut\"" }, 0.1),
    // Light leak sweep.
    t.to(`#${leakId}`, { x: "120%", duration: 1.0, ease: "raw:\"power2.inOut\"" }, 0.4),
    // Headline.
    t.to(`#${headId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.55),
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.85),
    t.to(`#${tcId}`, { opacity: 1, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.95),
  ];
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.05));
  tweens.push(t.to(`#${tagId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.25));

  // Subtle headline drift.
  const dwell = Math.max(0.6, clipDurationSec - 1.6);
  tweens.push(t.to(`#${headId}`, { scale: 1.03, duration: dwell, ease: "raw:\"sine.inOut\"" }, 1.4));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
